import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { GridLegacy as Grid } from "@mui/material";
import GroupIcon from "../../Assets/images/svg/Icons/Group 578.svg";
import { DeleteApiMethod, GetApiMethod } from "../../Utils/ApiService";
import "../../Components/Common/Styles/Formstyles.css";
import SuccessDialog, {
  useSuccessDialog,
} from "../../Components/Common/SuccesMessages/SuccessDialog";

interface AddonEntry {
  serviceId: string;
  cost: string;
  id?: string;
}

interface AddonsFormProps {
  formData: {
    providerId?: string;
    addons: AddonEntry[];
  };
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  handleNext: () => void;
  handleBack: () => void;
}

interface ServiceOption {
  label: string;
  id: string;
  type: string;
}

const AddonsForm: React.FC<AddonsFormProps> = ({
  formData,
  setFormData,
  handleNext,
  handleBack,
}) => {
  const [addons, setAddons] = useState<AddonEntry[]>([{ serviceId: "", cost: "" }]);
  const [options, setOptions] = useState<ServiceOption[]>([]);
  const [loading, setLoading] = useState(true);
  const { open: successOpen, message, showSuccess, closeSuccess } =
    useSuccessDialog();

  // 🔹 Fetch available service options
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await GetApiMethod("service/list");
        const allRecords = res?.data?.data?.records ?? [];
        const formatted = allRecords.map((item: any) => ({
          label: item.name || item.serviceName,
          id: item.id,
          type: item.type,
        }));
        setOptions(formatted);
      } catch (err) {
        console.error("Error fetching services:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  // 🔹 Fetch provider addons in edit mode
  useEffect(() => {
    const fetchProviderData = async () => {
      if (!formData.providerId) return;

      try {
        const res = await GetApiMethod(`provider/${formData.providerId}`);
        const providerServices = res?.data?.data?.providerServices || [];

        const savedAddons = providerServices
          .filter((s: any) => s.type === "Addons")
          .map((s: any) => ({
            id: s.id,
            serviceId: s.serviceId,
            cost: String(s.cost),
          }));

        setFormData((prev: any) => ({ ...prev, addons: savedAddons }));


        const finalAddons =
          savedAddons.length > 0 ? savedAddons : [{ serviceId: "", cost: "" }];

        setAddons(finalAddons);
        setFormData((prev: any) => ({ ...prev, addons: finalAddons }));
      } catch (err) {
        console.error("Error fetching provider data:", err);
      }
    };

    fetchProviderData();
  }, [formData.providerId, setFormData]);
  // After fetching service options and formData.addons
  // 🔹 Initialize addons only once, after serviceOptions and formData.addons are ready
  useEffect(() => {
    if (!formData.providerId || options.length === 0) return;

    const savedAddons = formData.addons?.length
      ? formData.addons
      : [{ serviceId: "", cost: "" }];

    setAddons(savedAddons);
  }, [formData.providerId, options, formData.addons]);


  // 🔹 Sync local addons whenever formData.addons updates (important for edit mode)
  useEffect(() => {
    if (formData.addons && formData.addons.length > 0) {
      setAddons(formData.addons);
    }
  }, [formData.addons]);

  // 🔹 Handle field changes
  const handleAddonChange = (index: number, field: keyof AddonEntry, value: string) => {
    const updated = [...addons];
    updated[index][field] = value;
    setAddons(updated);
    setFormData((prev: any) => ({ ...prev, addons: updated }));
  };

  // 🔹 Add / Remove row
  const addAddon = () => {
    const updated = [...addons, { serviceId: "", cost: "" }];
    setAddons(updated);
    setFormData((prev: any) => ({ ...prev, addons: updated }));
  };

const removeAddon = async (index: number) => {
  // if (!window.confirm("Are you sure you want to delete this addon?")) return;

  const target = addons[index];

  // 🔹 If this addon exists in backend (has id), delete it
  if (target?.id) {
    try {
      await DeleteApiMethod(`provider-service/delete/${target.id}`);
      showSuccess("Addon deleted successfully!");
    } catch (err) {
      console.error("Error deleting addon:", err);
      showSuccess("Failed to delete addon. Please try again.");
      return;
    }
  }

  // 🔹 Update local state after successful deletion
  const updated = addons.filter((_, i) => i !== index);
  setAddons(updated);
  setFormData((prev: any) => ({ ...prev, addons: updated }));
};
  // 🔹 Validate and move next
  const handleNextClick = () => {
    const valid = addons.every((a) => a.serviceId && a.cost);
    if (!valid) {
      showSuccess("Please fill all addon and cost fields before continuing.");
      return;
    }
    // showSuccess("Addons validated successfully!");
    setTimeout(() => {
      closeSuccess();
      handleNext();
    }, 800);
  };
  console.log("addonoptions+++++++++",options)
  const addonOptions = options.filter((o) => o.type === "Addons");

  return (
    <Box>
      {addons.map((entry, index) => (
        <Box key={index} mb={3}>
          <Grid container spacing={3} alignItems="flex-end">
            <Grid item xs={12} md={4}>
              <Typography className="form-input">Addons</Typography>
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                <Select
                  className="form-input"
                  value={entry.serviceId}
                  onChange={(e) =>
                    handleAddonChange(index, "serviceId", e.target.value)
                  }
                  fullWidth
                  variant="standard"
                  displayEmpty
                >
                  <MenuItem value="">Choose</MenuItem>
                  {addonOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography className="form-input">Cost</Typography>
              <TextField
                className="form-input"
                value={entry.cost}
                onChange={(e) => handleAddonChange(index, "cost", e.target.value)}
                fullWidth
                variant="standard"
                placeholder="₹ | Type here"
                type="number"
              />
            </Grid>

            <Grid item xs={12} md={3} display="flex" alignItems="center">
              {index === 0 ? (
                <Button onClick={addAddon} className="Add-new">
                  + Add More Addons
                </Button>
              ) : (
                <IconButton
                  onClick={() => removeAddon(index)}
                  color="error"
                  size="small"
                  sx={{ mt: 2 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Grid>
          </Grid>
        </Box>
      ))}

      <Box display="flex" justifyContent="start" mt={2}>
        <Button onClick={handleBack} className="btn-back">
          <Box component="img" src={GroupIcon} sx={{ mr: 1 }} />
          Back
        </Button>

        <Button onClick={handleNextClick} className="btn-next" sx={{ ml: 2 }}>
          Next
        </Button>
      </Box>

      <SuccessDialog open={successOpen} onClose={closeSuccess} message={message} />
    </Box>
  );
};

export default AddonsForm;
